import React from "react";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Grid from "components/common/Grid";
import { usePhcTheme } from "../PhcThemeContext";

const SnapshotCard = ({
  value,
  themeKey,
  valueColor,
  fullWidth,
  mobileLabel,
  desktopLabel,
  label,
}) => {
  const { theme, snapshotThemes, mode } = usePhcTheme();
  const cardTheme = snapshotThemes[themeKey] || snapshotThemes.invested;
  const isBetaMobile = fullWidth && themeKey === "beta";
  const labelMobile = mobileLabel || label;
  const labelDesktop = desktopLabel || label;
  const isLight = mode === "light";

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: {
          xs: isBetaMobile ? 100 : 146,
          md: 240,
        },
        borderRadius: { xs: "12px", md: theme.radiusCard },
        overflow: "hidden",
        border: {
          xs: theme.mobileCardBorder,
          md: "none",
        },
        borderTop: {
          md: `7px solid ${cardTheme.topBorder}`,
        },
        boxShadow: theme.cardShadow,
        backgroundImage: {
          xs: "none",
          md: theme.sectionBg,
        },
        backgroundColor: {
          xs: theme.mobileCardBg,
          md: theme.cardBg,
        },
        backdropFilter: { xs: isLight ? "none" : "blur(6px)", md: "none" },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: {
          xs: 1,
          md: themeKey === "beta" ? "10px" : 1.25,
        },
        px: {
          xs: "17px",
          md: themeKey === "beta" ? "36px" : 2,
        },
        py: {
          xs: "17px",
          md: themeKey === "beta" ? "42px" : 5,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: { xs: "none", md: theme.insetGlow },
        },
      }}
    >
      <Box
        sx={{
          width: { xs: 48, md: 90 },
          height: { xs: 48, md: 90 },
          borderRadius: { xs: "12px", md: "16px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage:
            cardTheme.fullIcon || cardTheme.iconBg === "transparent"
              ? "none"
              : cardTheme.iconBg,
          bgcolor: cardTheme.fullIcon
            ? "transparent"
            : isLight
            ? theme.snapshotIconBg || "#FFFFFF"
            : {
                xs:
                  themeKey === "invested"
                    ? "rgba(30,58,138,0.4)"
                    : themeKey === "current"
                      ? "rgba(69,222,231,0.2)"
                      : themeKey === "returns"
                        ? "rgba(6,78,59,0.3)"
                        : themeKey === "stocks"
                          ? "rgba(113,63,18,0.3)"
                          : "rgba(88,28,135,0.3)",
                md: "transparent",
              },
          boxShadow: cardTheme.fullIcon
            ? "none"
            : isLight
              ? theme.snapshotIconShadow
              : "none",
          border: isLight
            ? "none"
            : {
                xs:
                  themeKey === "invested"
                    ? "1px solid rgba(59,130,246,0.2)"
                    : themeKey === "current"
                      ? "1px solid rgba(69,222,231,0.2)"
                      : themeKey === "returns"
                        ? "1px solid rgba(16,185,129,0.2)"
                        : themeKey === "stocks"
                          ? "1px solid rgba(234,179,8,0.2)"
                          : "1px solid rgba(168,85,247,0.2)",
                md: "none",
              },
          flexShrink: 0,
          overflow: "hidden",
          mb: { xs: 0.5, md: 0 },
        }}
      >
        <Box
          component="img"
          src={cardTheme.icon}
          alt=""
          sx={{
            width: cardTheme.fullIcon
              ? { xs: 28, md: "100%" }
              : { xs: 20, md: 42 },
            height: cardTheme.fullIcon
              ? { xs: 28, md: "100%" }
              : { xs: 20, md: 42 },
            objectFit: "contain",
            display: "block",
          }}
        />
      </Box>

      <Stack
        spacing={{ xs: 0.25, md: themeKey === "beta" ? "4px" : 0.5 }}
        alignItems="center"
        sx={{ width: "100%" }}
      >
        <Text
          sx={{
            display: { xs: "block", md: "none" },
            fontWeight: 600,
            fontSize: 14,
            lineHeight: "16px",
            letterSpacing: "0.7px",
            color: theme.mobileLabelColor,
            textAlign: "center",
          }}
        >
          {labelMobile}
        </Text>
        <Text
          sx={{
            fontWeight: {
              xs:
                themeKey === "returns" ||
                themeKey === "stocks" ||
                themeKey === "beta"
                  ? 700
                  : 600,
              md: 700,
            },
            fontSize: {
              xs: themeKey === "invested" || themeKey === "current" ? 18 : 24,
              md: 24,
            },
            lineHeight: {
              xs:
                themeKey === "invested" || themeKey === "current"
                  ? "28px"
                  : "32px",
              md: "normal",
            },
            color: valueColor || {
              xs: theme.mobileValueColor,
              md: theme.textPrimary,
            },
            textAlign: "center",
            wordBreak: "break-word",
            whiteSpace: { md: "nowrap" },
          }}
        >
          {value}
        </Text>
        <Text
          sx={{
            display: { xs: "none", md: "block" },
            fontWeight: 400,
            fontSize: 16,
            lineHeight: "normal",
            color: theme.textPrimary,
            textAlign: "center",
            whiteSpace: "nowrap",
          }}
        >
          {labelDesktop}
        </Text>
      </Stack>
    </Box>
  );
};

/**
 * Portfolio Snapshot — desktop Figma 5917:2992 / light 9862:20997 / mobile 5626:88654
 */
const PortfolioSnapshot = ({ items = [] }) => {
  const { theme } = usePhcTheme();

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: { xs: 0, md: theme.radiusSection },
        border: {
          xs: "none",
          md: `1px solid ${theme.sectionBorder}`,
        },
        boxShadow: { xs: "none", md: theme.cardShadow },
        backgroundImage: { xs: "none", md: theme.sectionBg },
        backgroundColor: { xs: "transparent", md: theme.cardBg },
        px: { xs: 0, md: 2.5 },
        py: { xs: 0, md: 3 },
        overflow: "hidden",
        "&::after": {
          content: { xs: "none", md: '""' },
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: theme.sectionInsetGlow,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          position: "relative",
          zIndex: 1,
          mb: { xs: 3, md: 3 },
        }}
      >
        <Text
          sx={{
            fontWeight: { xs: 600, md: 600 },
            fontSize: { xs: 24, md: 24 },
            lineHeight: { xs: "32px", md: 1.2 },
            color: { xs: theme.mobileValueColor, md: theme.textPrimary },
          }}
        >
          Portfolio Snapshot
        </Text>
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            bgcolor: "rgba(69,222,231,0.1)",
            px: 1.5,
            py: 0.5,
            borderRadius: "9999px",
          }}
        >
          <Text
            sx={{
              fontWeight: 600,
              fontSize: 14,
              color: "#45DEE7",
              letterSpacing: "0.7px",
              lineHeight: "16px",
            }}
          >
            Live
          </Text>
        </Box>
      </Stack>

      <Grid
        container
        spacing={{ xs: 2, md: 1.5 }}
        sx={{ position: "relative", zIndex: 1, width: "100%" }}
      >
        {items.map((item) => {
          const isBeta = item.key === "beta" || item.themeKey === "beta";
          return (
            <Grid
              item
              xs={12}
              sm={12}
              md={4}
              lg={2.4}
              key={item.key}
            >
              <SnapshotCard
                value={item.value}
                label={
                  item.themeKey === "stocks"
                    ? undefined
                    : item.label
                }
                mobileLabel={
                  item.themeKey === "stocks" ? "Stocks" : item.label
                }
                desktopLabel={item.label}
                themeKey={item.themeKey}
                valueColor={item.valueColor}
                fullWidth={isBeta}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default PortfolioSnapshot;
